import React, { FC, useContext, useState, useEffect } from 'react';
import momentTz from 'moment-timezone';
import styled from 'styled-components';
import get from 'lodash/get';
import set from 'lodash/set';

import ADDatePicker from '@ad-scripts/presentation/components/common/datetime/date_picker';
import FormContext from 'src/presentation/contexts/FormContext';
import { ControlWrapper } from '@ad-scripts/presentation/components/common/forms/control_container';
import {
  TimeSelector,
  IntervalPart,
  StyledTimeSelectBox,
} from 'src/presentation/components/pages/shared/HoursTable/TimeRange';
import find from 'lodash/find';

export { IntervalPart };

type DateTimeRangeProps = {
  'data-aid': string;
  attr: string;
  intervalPart: IntervalPart;
  isInvalid: boolean;
};

const DateTimeControl: FC<DateTimeRangeProps> = ({ 'data-aid': dataAid, attr, intervalPart, isInvalid }) => {
  const { workingState, updateState, resetError, errors } = useContext(FormContext);

  const tz = (workingState as any)?.officeHours?.timezone;
  const value = get(workingState, attr);

  const error = find(errors, error => error.attr === attr);

  const [date, setDate] = useState('');
  const [time, setTime] = useState('');

  useEffect(() => {
    if (!value || !tz) {
      return;
    }

    const date = momentTz.utc(value).tz(tz);

    setDate(date.format('MM/DD/YYYY'));
    setTime(date.format('HH:mm'));
  }, [value, tz]);

  const handleDateChange = value => {
    if (!value) {
      setDate('');
      updateState(set({}, attr, null));
      return;
    }
    const formattedValue = value.format('MM/DD/YYYY');
    setDate(formattedValue);
    handleDateTimeChange({ newDate: formattedValue });
  };

  const handleTimeChange = ({ value }) => {
    if (!value) {
      setTime('');
      updateState(set({}, attr, null));
      return;
    }
    setTime(value);
    handleDateTimeChange({ newTime: value });
  };

  const handleDateTimeChange = ({ newDate, newTime }: { newDate?: string; newTime?: string }) => {
    const d = newDate || date;
    const t = newTime || time;

    if (!d || !t) {
      return;
    }
    resetError({ attr, errors });

    const dateMoment = momentTz.tz(`${d} ${t}`, 'MM/DD/YYYY hh:mm', tz);

    updateState(set({}, attr, dateMoment.toISOString()));
  };

  return (
    <DateTimeContainer>
      <StyledTimeSelectorContainer>
        <ADDatePicker
          className="dateSelector"
          data-aid={`${dataAid}-date`}
          isError={!!error || isInvalid}
          value={date}
          onChange={handleDateChange}
          placeholder="00/00/0000"
        />
      </StyledTimeSelectorContainer>
      <StyledTimeSelector
        attr={attr}
        className="timeSelector"
        data-aid={`${dataAid}-time`}
        disabled={false}
        fullAttr={attr}
        hasError={!!error || isInvalid}
        hidePmSelector={false}
        intervalPart={intervalPart}
        onChange={handleTimeChange}
        value={time}
      />
    </DateTimeContainer>
  );
};

const DateTimeContainer = styled.div`
  align-items: center;
  display: flex;
`;

const StyledTimeSelectorContainer = styled.div`
  min-width: 100px;
  padding-right: 8px;
`;

const StyledTimeSelector = styled(TimeSelector)`
  min-width: 76px;
  ${ControlWrapper} {
    min-height: 32px;
  }

  ${StyledTimeSelectBox} {
    padding-top: 0px;

    input {
      height: 32px;

      &::placeholder {
        color: ${p => p.theme.colors.gray500};
      }
    }
  }
`;

export default DateTimeControl;
